import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { axiosClient } from '../../../core/axios-client'

const fetchDamageReports = createAsyncThunk(
  'damageReports/fetchDamageReports',
  async ({ filters = {} } = {}, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get('/damage-reports/operator/', {
        params: { ...filters }
      })
      return response.data
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message)
    }
  }
)

const fetchRentDamageReports = createAsyncThunk(
  'damageReports/fetchRentDamageReports',
  async ({ rent_id, filters = {} } = {}, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(
        `/damage-reports/rents/${rent_id}/`,
        { params: { ...filters } }
      )
      return response.data
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message)
    }
  }
)

const fetchDriverDamageReports = createAsyncThunk(
  'damageReports/fetchDriverDamageReports',
  async ({ driver_id, filters = {} } = {}, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(
        `/damage-reports/couriers/${driver_id}/`,
        { params: { ...filters } }
      )
      return response.data
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message)
    }
  }
)

const fetchBikeDamageReports = createAsyncThunk(
  'damageReports/fetchBikeDamageReports',
  async ({ bike_id, filters = {} } = {}, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(
        `/damage-reports/bikes/${bike_id}/`,
        { params: { ...filters } }
      )
      return response.data
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message)
    }
  }
)

const reviewDamageReport = createAsyncThunk(
  'damageReports/reviewDamageReport',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.patch(`/damage-reports/operator/${id}/review/`, { status })
      return response.data
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message)
    }
  }
)

const createDamageReport = createAsyncThunk(
  'damageReports/createDamageReport',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/damage-reports/', payload)
      return response.data
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message)
    }
  }
)

const initialState = {
  listItems: {
    items: [],
    page: 1,
    page_size: 10,
    count: 0
  },
  reports: [],
  loading: false,
  error: null,

  create: {
    loading: false,
    error: null,
    item: null,
  }
}

export const slice = createSlice({
  name: 'damageReports',
  initialState,
  reducers: {
    setListItems(state, { payload }) {
      state.listItems = { ...state.listItems, ...payload }
      state.reports = state.listItems.items
    },
    resetListItems(state) {
      state.listItems = { items: [], page: 1, page_size: 10, count: 0 }
      state.reports = []
    },

    resetCreateDamageReport(state) {
      state.create = { loading: false, error: null, item: null }
    }
  },
  extraReducers: (builder) => {
    const applyListPayload = (state, payload) => {
      if (!payload) {
        state.listItems.items = []
        state.listItems.count = 0
        state.reports = []
        return
      }
      const { count = 0, results = null } = payload
      const items = Array.isArray(results)
        ? results
        : Array.isArray(payload)
          ? payload
          : (payload.results || [])
      state.listItems = {
        ...state.listItems,
        items,
        count
      }
      state.reports = items
    }

    builder
      .addCase(fetchDamageReports.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchDamageReports.fulfilled, (state, { payload }) => {
        state.loading = false
        applyListPayload(state, payload)
      })
      .addCase(fetchDamageReports.rejected, (state, { payload }) => {
        state.loading = false
        state.error = payload
      })

      .addCase(fetchRentDamageReports.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchRentDamageReports.fulfilled, (state, { payload }) => {
        state.loading = false
        applyListPayload(state, payload)
      })
      .addCase(fetchRentDamageReports.rejected, (state, { payload }) => {
        state.loading = false
        state.error = payload
      })

      .addCase(fetchDriverDamageReports.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchDriverDamageReports.fulfilled, (state, { payload }) => {
        state.loading = false
        applyListPayload(state, payload)
      })
      .addCase(fetchDriverDamageReports.rejected, (state, { payload }) => {
        state.loading = false
        state.error = payload
      })

      .addCase(fetchBikeDamageReports.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchBikeDamageReports.fulfilled, (state, { payload }) => {
        state.loading = false
        applyListPayload(state, payload)
      })
      .addCase(fetchBikeDamageReports.rejected, (state, { payload }) => {
        state.loading = false
        state.error = payload
      })

      .addCase(reviewDamageReport.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(reviewDamageReport.fulfilled, (state, { payload }) => {
        state.loading = false
        const idx = state.listItems.items.findIndex(i => i.id === payload.id)
        if (idx !== -1) {
          state.listItems.items[idx] = payload
        }
        state.reports = state.listItems.items
      })
      .addCase(reviewDamageReport.rejected, (state, { payload }) => {
        state.loading = false
        state.error = payload
      })

      .addCase(createDamageReport.pending, (state) => {
        state.create.loading = true
        state.create.error = null
        state.create.item = null
      })
      .addCase(createDamageReport.fulfilled, (state, { payload }) => {
        state.create.loading = false
        state.create.item = payload
      })
      .addCase(createDamageReport.rejected, (state, { payload }) => {
        state.create.loading = false
        state.create.error = payload
      })
  }
})

export const { setListItems, resetListItems, resetCreateDamageReport } = slice.actions

export {
  fetchDamageReports,
  fetchRentDamageReports,
  fetchDriverDamageReports,
  fetchBikeDamageReports,
  reviewDamageReport,
  createDamageReport
}

export default slice.reducer
